import { useEexCapability } from "../components/eex/use-eex-capability";
import "../features/eex/operations-pages.css";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, selectedCompanyId } from "../lib/api";
import { invalidateEexData } from "../lib/eex-invalidate";
import { useAuth } from "../stores/auth";
import { cn } from "../lib/utils";
import { WarningList, errMessage } from "../components/eex/ui";
import { useEexRole } from "../components/eex/use-eex-role";
import { desktopOnlyRoute } from "../components/ui/mobile-availability-page";
import { EexAccessMatrix } from "../components/admin/eex-access-matrix";

export const Route = createFileRoute("/eex/settings")({
  validateSearch: (search: Record<string, unknown>): { section?: "access" } => ({ section: search.section === "access" ? "access" : undefined }),
  component: desktopOnlyRoute(EexSettingsPage),
});

interface EexAccount {
  id: string;
  name: string;
  kind: string;
  active: boolean;
}

function EexSettingsPage() {
  const { canWrite, isAdmin } = useEexRole();
  const canManageTrades = useEexCapability("trades.manage");
  const tab = Route.useSearch().section ?? "configuration";
  const navigate = Route.useNavigate();
  return (
    <div className="operations-workspace ops-page ops-settings p-4 space-y-6">
      <header className="ops-heading">
        <h1 className="font-semibold text-lg">EEX Settings</h1>
        <p className="text-muted text-xs max-w-3xl">
          Company configuration, user access and personal defaults.
        </p>
      </header>
      {isAdmin && <div role="tablist" aria-label="EEX settings sections" className="flex gap-4 border-b border-border">
        {([['configuration', 'Configuration'], ['access', 'User access']] as const).map(([id, label]) => <button key={id} id={`eex-settings-tab-${id}`} role="tab" aria-selected={tab === id} aria-controls={`eex-settings-panel-${id}`} tabIndex={tab === id ? 0 : -1} onClick={() => void navigate({ search: { section: id === "access" ? "access" : undefined } })} onKeyDown={event => {
          if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
          event.preventDefault();
          const next = event.key === "Home" ? "configuration" : event.key === "End" ? "access" : tab === "access" ? "configuration" : "access";
          void navigate({ search: { section: next === "access" ? "access" : undefined } });
          document.getElementById(`eex-settings-tab-${next}`)?.focus();
        }} className={cn("px-2 py-3 text-sm border-b-2", tab === id ? "border-accent text-accent" : "border-transparent text-muted")}>{label}</button>)}
      </div>}
      {isAdmin && tab === "access" ? <div id="eex-settings-panel-access" role="tabpanel" aria-labelledby="eex-settings-tab-access"><EexAccessMatrix /></div> : <div id="eex-settings-panel-configuration" role={isAdmin ? "tabpanel" : undefined} aria-labelledby={isAdmin ? "eex-settings-tab-configuration" : undefined} className="ops-settings-grid">
        <PlatformNameBar />
        {isAdmin && <AccountsAdmin />}
        {isAdmin && <CounterpartiesAdmin />}
        {isAdmin && <TraderIdentitiesAdmin />}
        {canWrite && <PrivacyPrefsBar />}
        {canManageTrades && <BulkPrivacyBar />}
        {canManageTrades && <ArchiveUploadBar />}
      </div>}
    </div>
  );
}

function PlatformNameBar() {
  const role = useAuth((s) => s.user?.role);
  const canEdit = role === "company_admin";
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");

  const settingsQ = useQuery({
    queryKey: ["eex-settings"],
    queryFn: () => api<{ platformName: string | null }>("/eex/settings"),
  });

  const mutation = useMutation({
    mutationFn: (platformName: string) =>
      api<{ platformName: string | null }>("/eex/settings", {
        method: "PUT",
        body: JSON.stringify({ platformName }),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["eex-settings"], data);
      setEditing(false);
    },
  });

  const platformName = settingsQ.data?.platformName ?? null;
  if (settingsQ.isLoading) return null;

  return (
    <div
      className={cn(
        "ops-settings-full ops-settings-card border rounded-md p-3 text-xs space-y-2",
        platformName ? "border-border/60" : "border-amber-500/60 bg-amber-500/5"
      )}
    >
      {!editing ? (
        <div className="flex items-center justify-between gap-3">
          <div>
            <span className="text-muted">Your company name in the export: </span>
            {platformName ? (
              <span className="font-mono">{platformName}</span>
            ) : (
              <span className="text-amber-600 dark:text-amber-400">
                not set — without it, Buy/Sell can only be derived from the Trade ID suffix
              </span>
            )}
          </div>
          {canEdit && (
            <button
              type="button"
              className="text-accent hover:underline shrink-0"
              onClick={() => {
                setValue(platformName ?? "");
                setEditing(true);
              }}
            >
              {platformName ? "Edit" : "Set"}
            </button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <input
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Company legal name exactly as in the export"
            className="flex-1 bg-transparent border border-border/60 rounded px-2 py-1 font-mono"
          />
          <button
            type="button"
            disabled={mutation.isPending}
            className="text-accent hover:underline disabled:opacity-50"
            onClick={() => mutation.mutate(value.trim())}
          >
            Save
          </button>
          <button type="button" className="text-muted hover:underline" onClick={() => setEditing(false)}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

function AccountsAdmin() {
  const qc = useQueryClient();
  const accountsQ = useQuery({
    queryKey: ["eex-accounts"],
    queryFn: () => api<{ accounts: EexAccount[] }>("/eex/accounts"),
  });
  const [name, setName] = useState("");
  const add = useMutation({
    mutationFn: (n: string) => api("/eex/accounts", { method: "POST", body: JSON.stringify({ name: n }) }),
    onSuccess: () => {
      setName("");
      void qc.invalidateQueries({ queryKey: ["eex-accounts"] });
    },
  });

  const accounts = accountsQ.data?.accounts ?? [];
  const have = new Set(accounts.map((a) => a.name.toLowerCase()));
  const suggestions = ["TRADING", "FURNIZARE"].filter((s) => !have.has(s.toLowerCase()));

  return (
    <div className="ops-panel ops-settings-card border border-border/60 rounded-md p-3 text-xs space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-muted">Accounts:</span>
        {accounts.length === 0 && <span className="text-muted italic">none yet — add TRADING / FURNIZARE below</span>}
        {accounts.map((a) => (
          <span key={a.id} className="px-1.5 py-0.5 rounded bg-border/20 font-mono">
            {a.name}
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && name.trim()) add.mutate(name.trim());
          }}
          placeholder="New account name"
          className="ops-settings-name-input bg-transparent border border-border/60 rounded px-2 py-1 font-mono"
        />
        <button
          type="button"
          disabled={!name.trim() || add.isPending}
          onClick={() => add.mutate(name.trim())}
          className="text-accent hover:underline disabled:opacity-50"
        >
          Add
        </button>
        {suggestions.map((s) => (
          <button key={s} type="button" disabled={add.isPending} onClick={() => add.mutate(s)} className="text-muted hover:text-accent">
            + {s}
          </button>
        ))}
      </div>
    </div>
  );
}

interface EexCounterparty {
  id: string;
  name: string;
  active: boolean;
}

// Managed counterparty list — the other side of bilateral/ISDA trades. Trades
// store the NAME (like book ↔ accounts), so the list is the controlled
// vocabulary the bilateral entry form validates against.
function CounterpartiesAdmin() {
  const qc = useQueryClient();
  const listQ = useQuery({
    queryKey: ["eex-counterparties"],
    queryFn: () => api<{ counterparties: EexCounterparty[] }>("/eex/counterparties"),
  });
  const [name, setName] = useState("");
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["eex-counterparties"] });
  const add = useMutation({
    mutationFn: (n: string) =>
      api("/eex/counterparties", { method: "POST", body: JSON.stringify({ name: n }) }),
    onSuccess: () => {
      setName("");
      invalidate();
    },
  });
  const toggle = useMutation({
    mutationFn: (v: { id: string; active: boolean }) =>
      api(`/eex/counterparties/${v.id}`, {
        method: "PATCH",
        body: JSON.stringify({ active: v.active }),
      }),
    onSuccess: invalidate,
  });

  const counterparties = listQ.data?.counterparties ?? [];

  return (
    <div className="ops-panel ops-settings-card border border-border/60 rounded-md p-3 text-xs space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-muted">Counterparties (bilateral/ISDA):</span>
        {counterparties.length === 0 && (
          <span className="text-muted italic">none yet — needed before entering bilateral trades</span>
        )}
        {counterparties.map((c) => (
          <span
            key={c.id}
            className={cn(
              "px-1.5 py-0.5 rounded bg-border/20 font-mono inline-flex items-center gap-1",
              !c.active && "opacity-50 line-through"
            )}
          >
            {c.name}
            <button
              type="button"
              title={c.active ? "Deactivate" : "Reactivate"}
              disabled={toggle.isPending}
              onClick={() => toggle.mutate({ id: c.id, active: !c.active })}
              className="text-muted hover:text-negRed no-underline"
            >
              {c.active ? "×" : "↺"}
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && name.trim()) add.mutate(name.trim());
          }}
          placeholder="New counterparty (e.g. AXPO)"
          className="ops-settings-name-input bg-transparent border border-border/60 rounded px-2 py-1 font-mono"
        />
        <button
          type="button"
          disabled={!name.trim() || add.isPending}
          onClick={() => add.mutate(name.trim())}
          className="text-accent hover:underline disabled:opacity-50"
        >
          Add
        </button>
      </div>
    </div>
  );
}

// Maps each EEX execution code (eex_trader_code) -> the app user trades carrying
// it should be attributed to. New imports whose code is mapped are auto-attributed.
function TraderIdentitiesAdmin() {
  const qc = useQueryClient();
  const codesQ = useQuery({
    queryKey: ["eex-trader-codes"],
    queryFn: () => api<{ codes: string[] }>("/eex/trader-codes"),
  });
  const idsQ = useQuery({
    queryKey: ["eex-trader-identities"],
    queryFn: () =>
      api<{ identities: { id: string; code: string; userId: string; label: string }[] }>(
        "/eex/trader-identities"
      ),
  });
  const usersQ = useQuery({
    queryKey: ["eex-company-users"],
    queryFn: () => api<{ users: { id: string; label: string; role: string }[] }>("/eex/users"),
  });
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["eex-trader-identities"] });
  const setMap = useMutation({
    mutationFn: (v: { code: string; userId: string }) =>
      api("/eex/trader-identities", { method: "POST", body: JSON.stringify(v) }),
    onSuccess: invalidate,
  });
  const delMap = useMutation({
    mutationFn: (id: string) => api(`/eex/trader-identities/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });

  const identities = idsQ.data?.identities ?? [];
  const byCode = new Map(identities.map((i) => [i.code.toLowerCase(), i]));
  const users = usersQ.data?.users ?? [];
  // Union of codes seen on trades + any mapped code no longer on a live trade.
  const codeSet = new Set<string>(codesQ.data?.codes ?? []);
  for (const i of identities) codeSet.add(i.code);
  const codes = [...codeSet].sort((a, b) => a.localeCompare(b));

  return (
    <div className="ops-panel ops-settings-full ops-settings-card border border-border/60 rounded-md p-3 text-xs space-y-2">
      <div className="ops-section-title font-semibold text-sm">Trader identities</div>
      <p className="text-muted">
        Map each EEX execution code to the trader it should be attributed to. New imports whose code
        is mapped are auto-attributed (confirmed) to that user instead of whoever pasted them.
      </p>
      {codes.length === 0 && <div className="text-muted italic">No EEX codes seen yet.</div>}
      <div className="space-y-1">
        {codes.map((code) => {
          const mapped = byCode.get(code.toLowerCase());
          return (
            <div key={code} className="flex items-center gap-2">
              <span className="font-mono w-48 truncate" title={code}>
                {code}
              </span>
              <span className="text-muted">→</span>
              <select
                value={mapped?.userId ?? ""}
                disabled={setMap.isPending || delMap.isPending}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v) setMap.mutate({ code, userId: v });
                  else if (mapped) delMap.mutate(mapped.id);
                }}
                className="bg-transparent border border-border/60 rounded px-2 py-1"
              >
                <option value="">— unmapped —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.label}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PrivacyPrefsBar() {
  const queryClient = useQueryClient();
  const prefsQ = useQuery({
    queryKey: ["eex-prefs"],
    queryFn: () => api<{ hideAttribution: boolean; hideTrades: boolean }>("/eex/preferences"),
  });
  const mutation = useMutation({
    mutationFn: (body: { hideAttribution?: boolean; hideTrades?: boolean }) =>
      api<{ hideAttribution: boolean; hideTrades: boolean }>("/eex/preferences", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["eex-prefs"], data);
      void queryClient.invalidateQueries({ queryKey: ["eex-trades"] });
    },
  });

  const prefs = prefsQ.data;
  if (!prefs) return null;

  return (
    <div className="ops-panel ops-settings-full ops-settings-card border border-border/60 rounded-md p-3 text-xs flex flex-wrap items-center gap-x-6 gap-y-2">
      <span className="text-muted">Privacy defaults for future trades assigned to you:</span>
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={prefs.hideAttribution}
          disabled={mutation.isPending}
          onChange={(e) => mutation.mutate({ hideAttribution: e.target.checked })}
        />
        Hide my name in attribution
      </label>
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={prefs.hideTrades}
          disabled={mutation.isPending}
          onChange={(e) => mutation.mutate({ hideTrades: e.target.checked })}
        />
        Hide my trades from others (admins still see them)
      </label>
    </div>
  );
}

function BulkPrivacyBar() {
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<boolean | null>(null);
  const [updateDefault, setUpdateDefault] = useState(true);
  const mutation = useMutation({
    mutationFn: (isPrivate: boolean) => api<{
      updatedTrades: number; isPrivate: boolean; defaultUpdated: boolean;
    }>("/eex/trades/privacy/bulk", {
      method: "POST", body: JSON.stringify({ isPrivate, updateDefault }),
    }),
    onSuccess: () => { setTarget(null); invalidateEexData(queryClient); },
  });
  const choose = (value: boolean) => { mutation.reset(); setTarget(value); };
  return (
    <section aria-label="Bulk trade privacy" className="ops-panel ops-settings-full ops-settings-card border border-border/60 rounded-md p-3 text-xs space-y-3">
      <h2 className="ops-section-title font-semibold">Privacy for all my trades</h2>
      <p className="text-muted">
        Apply to every existing trade assigned to you in this company, across all dates and pages.
        Trades you uploaded for other traders and unassigned trades are excluded.
        You and admins can always see your trades. Name visibility stays unchanged.
      </p>
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={updateDefault} disabled={mutation.isPending}
          onChange={(e) => setUpdateDefault(e.target.checked)} />
        Also use this setting for future trades assigned to me
      </label>
      {target === null ? (
        <div className="flex flex-wrap gap-3">
          <button type="button" className="border border-border rounded px-3 py-2" onClick={() => choose(true)}>
            Make all my trades private
          </button>
          <button type="button" className="border border-border rounded px-3 py-2" onClick={() => choose(false)}>
            Make all my trades visible
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <p>{target
            ? "Hide all existing trades assigned to you from colleagues? Admins retain access."
            : "Allow colleagues with EEX access to see all existing trades assigned to you?"}
            {updateDefault ? " The same setting will apply to future trades." : " Your future-trade default will stay unchanged."}
          </p>
          <div className="flex gap-3">
            <button type="button" disabled={mutation.isPending} className="border border-border rounded px-3 py-2 disabled:opacity-50"
              onClick={() => mutation.mutate(target)}>{mutation.isPending ? "Applying…" : "Apply to all my trades"}</button>
            <button type="button" disabled={mutation.isPending} onClick={() => setTarget(null)}>Cancel</button>
          </div>
        </div>
      )}
      {mutation.isError && <p role="alert" className="text-red-500">{errMessage(mutation.error)}</p>}
      {mutation.isSuccess && <p role="status">
        {mutation.data.updatedTrades.toLocaleString()} trades changed to {mutation.data.isPrivate ? "private" : "visible"}.
        {mutation.data.defaultUpdated ? ` Future trades will be ${mutation.data.isPrivate ? "private" : "visible"}.` : " Future-trade default unchanged."}
      </p>}
    </section>
  );
}

interface ArchiveSheetSummary {
  sheet: string;
  parsed: number;
  inserted: number;
  duplicates: number;
  skipped: number;
  warnings: string[];
}

// Traders' transaction-history workbook (one sheet per trader book, same
// platform columns as the paste, wrapped in P&L scratch rows). Same dedup and
// attribution rules as pasting — re-importing is harmless.
function ArchiveUploadBar() {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ filename: string; sheets: ArchiveSheetSummary[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState(0);

  const importFile = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const companyId = selectedCompanyId();
      const res = await fetch("/api/eex/trades/archive", {
        method: "POST",
        credentials: "include",
        headers: companyId ? { "x-company-id": companyId } : undefined,
        body: fd,
      });
      const body = (await res.json()) as { filename: string; sheets: ArchiveSheetSummary[]; error?: string };
      if (!res.ok) throw new Error(body.error ?? res.statusText);
      setResult(body);
      setFile(null);
      setInputKey((k) => k + 1);
      void qc.invalidateQueries({ queryKey: ["eex-trades"] });
      invalidateEexData(qc);
      void qc.invalidateQueries({ queryKey: ["eex-contracts"] });
      void qc.invalidateQueries({ queryKey: ["eex-pair-labels"] });
      void qc.invalidateQueries({ queryKey: ["eex-trader-codes"] });
      void qc.invalidateQueries({ queryKey: ["eex-trader-identities"] });
      void qc.invalidateQueries({ queryKey: ["eex-allocatable"] });
      void qc.invalidateQueries({ queryKey: ["eex-margin"] });
      void qc.invalidateQueries({ queryKey: ["eex-delivery"] });
    } catch (e) {
      setError(errMessage(e));
    }
    setBusy(false);
  };

  return (
    <div className="ops-panel ops-settings-full ops-settings-card border border-border/60 rounded-md p-3 text-xs space-y-2">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-muted">Traders&rsquo; archive (.xlsx, one sheet per book):</span>
        <input
          key={inputKey}
          type="file"
          accept=".xlsx"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="text-xs"
        />
        <button
          type="button"
          disabled={!file || busy}
          onClick={() => void importFile()}
          className="ops-primary rounded px-3 py-1 bg-accent text-white disabled:opacity-40"
        >
          {busy ? "Importing…" : "Import archive"}
        </button>
      </div>
      {error && <div className="text-negRed">{error}</div>}
      {result && (
        <div className="font-mono space-y-0.5">
          {result.sheets.map((s) => (
            <div key={s.sheet}>
              {s.sheet}: parsed {s.parsed} · inserted {s.inserted} · duplicates {s.duplicates} · skipped {s.skipped}
            </div>
          ))}
          {result.sheets.some((s) => s.warnings.length > 0) && (
            <WarningList warnings={result.sheets.flatMap((s) => s.warnings.map((w) => `${s.sheet}: ${w}`))} />
          )}
        </div>
      )}
    </div>
  );
}
